import { computed, DestroyRef, inject, Service, signal, WritableSignal } from '@angular/core';

import { ActionState } from '@shared/state/async-action';
import { PlatformService } from '@core/platform/platform.service';
import { OrganizationsRepository } from '../domain/interfaces/organizations.interface';
import {
  EMPTY_ORG_ENGAGEMENT,
  OrgChallenge,
  OrgEngagement,
  OrgEngagementOverview,
  OrgProgressOp,
  OrgPulse,
  exceedsDailyCap,
  reasonMeansStaleTab,
} from '../domain/models/org-engagement.model';
import { OrgError } from '../domain/org.errors';
import { OrgErrorMapper } from './org-error.mapper';
import { destroyNotifier, firstValueFromScoped, runOrgAction } from './org-action';

/**
 * OrgEngagementFacade — «منظومة الانخراط» (§4).
 *
 * One GET builds the whole tab; three writes update parts of it. The writes are
 * where the care is:
 *
 *  · Trap 8 — `join` and `pulse` answer with PARTIAL, differently-shaped
 *    payloads. Both are merged FIELD-WISE here ({@link mergeOverview},
 *    {@link mergePulse}); assigning either over the whole state would blank the
 *    badges and the pulse, or replace the `{days,label}` streak with a bare int.
 *  · Trap 9 — `op` must be picked once and kept. This facade uses `set`
 *    everywhere, because the only input it offers is pre-filled from
 *    `today_value`, which is exactly the shape §4.4 says `set` is for. Mixing in
 *    an `add` affordance later would double-count.
 *  · Trap 10 — `above_max` REJECTS rather than clamping, so a value over the cap
 *    is refused before it leaves the browser, with the cap named in the message.
 */
@Service({ autoProvided: false })
export class OrgEngagementFacade {
  private readonly repo = inject(OrganizationsRepository);
  private readonly errors = inject(OrgErrorMapper);
  private readonly platform = inject(PlatformService);
  private readonly destroyed = destroyNotifier(inject(DestroyRef));

  private readonly _engagement = signal<OrgEngagement>(EMPTY_ORG_ENGAGEMENT);
  private readonly _loadError = signal<OrgError | null>(null);
  private readonly _joinError = signal<OrgError | null>(null);
  private readonly _progressError = signal<OrgError | null>(null);
  private readonly _pulseError = signal<OrgError | null>(null);
  private readonly _loadedAt = signal(0);
  /** The challenge a per-row action is running for — so only that row shows busy. */
  private readonly _busyChallengeId = signal<number | null>(null);

  readonly load = new ActionState('reference_portal_org_engagement_failed', 'GET /org/engagement');
  readonly join = new ActionState('reference_portal_org_join_failed', 'POST /challenges/{id}/join');
  readonly report = new ActionState('reference_portal_org_progress_failed', 'POST /challenges/{id}/progress');
  readonly pulse = new ActionState('reference_portal_org_pulse_failed', 'POST /engagement/pulse');

  readonly engagement = this._engagement.asReadonly();
  readonly loadError = this._loadError.asReadonly();
  readonly joinError = this._joinError.asReadonly();
  readonly progressError = this._progressError.asReadonly();
  readonly pulseError = this._pulseError.asReadonly();
  readonly busyChallengeId = this._busyChallengeId.asReadonly();

  readonly isFirstLoad = computed(() => this._loadedAt() === 0 && !this.load.failed());
  readonly challenges = computed(() => this._engagement().challenges);
  readonly streak = computed(() => this._engagement().streak);
  /** Always exactly four, always in this order. */
  readonly badges = computed(() => this._engagement().badges);
  readonly pulseBlock = computed(() => this._engagement().pulse);
  /** NULL when the org has no active question, or for a non-member — check before drawing. */
  readonly pulseQuestion = computed(() => this._engagement().pulse.question);
  readonly hasChallenges = computed(() => this._engagement().challenges.length > 0);

  // ── reads ───────────────────────────────────────────────────────────────
  async ensureLoaded(): Promise<void> {
    if (!this.platform.isBrowser || this._loadedAt() > 0 || this.load.loading()) return;
    await this.reload();
  }

  async reload(): Promise<void> {
    const data = await runOrgAction(this.load, this._loadError, this.errors, () =>
      firstValueFromScoped(this.repo.getEngagement(), this.destroyed),
    );
    if (data) {
      this._engagement.set(data);
      this._loadedAt.set(Date.now());
    }
  }

  // ── writes ──────────────────────────────────────────────────────────────
  /**
   * Join a challenge. Joining twice is NOT an error — it returns the existing
   * participation — so the button stays enabled and idempotent rather than
   * guessing at local state.
   */
  async joinChallenge(challengeId: number): Promise<void> {
    this._busyChallengeId.set(challengeId);
    try {
      const overview = await runOrgAction(this.join, this._joinError, this.errors, () =>
        firstValueFromScoped(this.repo.joinChallenge(challengeId), this.destroyed),
      );
      if (overview) this.mergeOverview(overview);
      else await this.reloadIfStale(this._joinError);
    } finally {
      this._busyChallengeId.set(null);
    }
  }

  /**
   * Report a self-reported value. `op` is fixed to `set` for the reason in this
   * class's doc comment; `date` is omitted so the server's "today" applies.
   *
   * Returns true when the write landed, so a caller can close its input only on
   * success and leave the typed value in place on a refusal.
   */
  async reportProgress(challenge: OrgChallenge, value: number, date?: string): Promise<boolean> {
    // Trap 10: the cap REJECTS, it does not clamp. Refusing here — with the cap
    // named — beats a round-trip that comes back 422 with the same information.
    if (exceedsDailyCap(challenge, value)) {
      this._progressError.set({
        status: 422,
        reason: 'above_max',
        message: this.errors.reasonMessage('above_max'),
        fieldErrors: {},
        max: challenge.maxDailyValue,
        retryAfter: null,
      });
      return false;
    }

    this._busyChallengeId.set(challenge.id);
    try {
      const op: OrgProgressOp = 'set';
      const result = await runOrgAction(this.report, this._progressError, this.errors, () =>
        firstValueFromScoped(this.repo.reportProgress(challenge.id, value, op, date), this.destroyed),
      );
      if (!result) {
        // §4.4: `not_yours` says "reload the tab", and `challenge_ended` means
        // the card on screen predates the end. Both are stale-data refusals, so
        // re-read rather than leaving the member staring at a card the server
        // has already moved on from. The error stays set — the reload explains
        // the refusal, it does not hide it.
        await this.reloadIfStale(this._progressError);
        return false;
      }
      // `overview` carries every card, so it is the authoritative merge; the
      // single `challenge` echo is already inside it.
      this.mergeOverview(result.overview);
      return true;
    } finally {
      this._busyChallengeId.set(null);
    }
  }

  /**
   * Answer the weekly pulse. Re-answering before the week ends OVERWRITES — a
   * pulse asks how the week is going and the week is not over — so this stays
   * available after an answer rather than locking.
   *
   * The response is the PULSE BLOCK ONLY (trap 8), merged field-wise.
   */
  async answerPulse(value: number): Promise<boolean> {
    const answered = await runOrgAction(this.pulse, this._pulseError, this.errors, () =>
      firstValueFromScoped(this.repo.answerPulse(value), this.destroyed),
    );
    if (!answered) return false;
    this.mergePulse(answered);
    return true;
  }

  /**
   * Health samples were credited to some metrics (§5.2 `credited_metrics`) —
   * their challenges were re-scored server-side, so the tab is stale. Re-read
   * only when something was actually credited.
   */
  async refreshAfterHealthSync(creditedMetrics: readonly string[]): Promise<void> {
    if (!creditedMetrics.length) return;
    const affected = this.challenges().some((c) => creditedMetrics.includes(c.metric));
    if (affected) await this.reload();
  }

  clearProgressError(): void {
    this._progressError.set(null);
    this.report.reset();
  }

  clearJoinError(): void {
    this._joinError.set(null);
    this.join.reset();
  }

  clearPulseError(): void {
    this._pulseError.set(null);
    this.pulse.reset();
  }

  // ─── internals ──────────────────────────────────────────────────────────

  /**
   * Merge the PARTIAL overview payload (trap 8). ONLY `challenges` and the streak
   * DAY COUNT are present in it — badges and pulse are absent, and `streakDays`
   * is a bare int with no label — so the existing streak label is kept rather
   * than blanked, and everything else is left untouched.
   */
  private mergeOverview(overview: OrgEngagementOverview): void {
    const current = this._engagement();
    this._engagement.set({
      ...current,
      challenges: overview.challenges.length ? overview.challenges : current.challenges,
      streak:
        overview.streakDays === current.streak.days
          ? current.streak
          : // No label came with the int, so keep the one we have rather than
            // rendering a bare number where a sentence belongs. The next full
            // `GET /engagement` restores the server's own wording.
            { days: overview.streakDays, label: current.streak.label },
    });
  }

  /**
   * Re-read the tab when a refusal means our copy of it is stale (§4.4's
   * `not_yours` / `challenge_ended`). Deliberately narrow: every other reason is
   * a refusal of the specific VALUE, and re-reading on those would discard the
   * member's typed number for no reason. The refusal itself is left in place so
   * they still see why the write did not land.
   */
  private async reloadIfStale(sink: WritableSignal<OrgError | null>): Promise<void> {
    const held = sink();
    if (!reasonMeansStaleTab(held?.reason ?? null)) return;
    await this.reload();
    // `reload()` writes only its own `_loadError` sink, so the refusal is
    // normally still here — but re-assert it rather than assume, so the reason
    // the member needs to read always survives the refresh it triggered.
    sink.set(held);
  }

  /** Merge the pulse-only payload, leaving challenges, streak and badges alone. */
  private mergePulse(pulse: OrgPulse): void {
    this._engagement.set({ ...this._engagement(), pulse });
  }
}
